'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBackdrop from '@/components/PageBackdrop';

function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

interface Course {
  id: string;
  title: string;
  description: string;
  price: string;
  level: string;
  duration: string;
  topics: string[];
  available: boolean;
}

const courses: Course[] = [
  {
    id: 'guide-to-programme-metal-drums',
    title: 'Guide to Programme Metal Drums',
    description:
      'The only guide you will ever need in order to programme realistic, professional sounding drums for your metal tracks! Get personalised feedback, tutorial videos and more!',
    price: 'FREE',
    level: 'Beginner - Intermediate',
    duration: '1.5 hours',
    topics: [
      'Drum Programming',
      'Metal Production',
      'Velocity Humanisation',
      'MIDI Editing',
      'Realistic Drums',
    ],
    available: true,
  },
  {
    id: 'metal-mixing-fundamentals',
    title: 'Metal Mixing Fundamentals',
    description: 'Master the core techniques behind a punishing, professional metal mix.',
    price: '£149',
    level: 'Beginner – Intermediate',
    duration: '6 hours',
    topics: ['Gain Staging', 'Guitar Tone Shaping', 'Parallel Compression', 'Bus Processing'],
    available: false,
  },
  {
    id: 'drum-recording-masterclass',
    title: 'Drum Recording Masterclass',
    description: 'Get the most out of every drum session with professional mic techniques.',
    price: '£129',
    level: 'Intermediate',
    duration: '5 hours',
    topics: ['Mic Placement', 'Room Acoustics', 'Drum Tuning', 'Overhead Techniques'],
    available: false,
  },
  {
    id: 'songwriting-for-metal',
    title: 'Songwriting for Metal',
    description: 'Craft songs that hit harder and mean more with proven songwriting techniques.',
    price: '£99',
    level: 'All Levels',
    duration: '4 hours',
    topics: ['Riff Construction', 'Song Structure', 'Lyric Writing', 'Arrangement'],
    available: false,
  },
  {
    id: 'advanced-metal-production',
    title: 'Advanced Metal Production',
    description: 'Take your productions to a professional level with advanced techniques.',
    price: '£199',
    level: 'Advanced',
    duration: '8 hours',
    topics: ['Sample Replacement', 'Stem Mixing', 'Mastering for Streaming', 'Production Workflow'],
    available: false,
  },
  {
    id: 'mix-ready-guitar-tones',
    title: 'Mix-Ready Guitar Tones',
    description: 'Stop fighting your guitar tones in the mix with expert dialling techniques.',
    price: '£109',
    level: 'Intermediate',
    duration: '4.5 hours',
    topics: ['Amp Sim Dialling', 'Cab IR Selection', 'DI Re-Amping', 'Guitar Layering'],
    available: false,
  },
];

const levelColors: Record<string, string> = {
  'Beginner - Intermediate': 'text-green-400 border-green-400/30 bg-green-400/5',
  'Beginner – Intermediate': 'text-green-400 border-green-400/30 bg-green-400/5',
  Intermediate: 'text-yellow-400 border-yellow-400/30 bg-yellow-400/5',
  Advanced: 'text-primary border-primary/30 bg-primary/5',
  'All Levels': 'text-blue-400 border-blue-400/30 bg-blue-400/5',
};

interface Testimonial {
  id: string;
  name: string;
  rating: number;
  text: string;
  course: string;
}

const testimonials: Testimonial[] = [
  {
    id: 't1',
    name: 'Ann Vanner',
    rating: 5,
    text: 'Really really good. As a complete beginner, I found this really clear, supportive, and easy to follow. Everything was explained without assumptions, and the production quality made it feel calm and professional rather than overwhelming. I came away feeling more confident and curious to learn more.',
    course: 'Guide to Programme Metal Drums',
  },
  {
    id: 't2',
    name: 'Xander Quirk',
    rating: 5,
    text: 'Absolutely brilliant, really helpful and great production quality too!',
    course: 'Guide to Programme Metal Drums',
  },
  {
    id: 't3',
    name: 'Luke Reid',
    rating: 5,
    text: 'Smashed it with this, definitely helped my knowledge into programming drums!',
    course: 'Guide to Programme Metal Drums',
  },
  {
    id: 't4',
    name: 'ella',
    rating: 5,
    text: 'really helpful course! particularly the "understanding velocities" video really helped me out!!',
    course: 'Guide to Programme Metal Drums',
  },
];

const overallRating = 5;
const totalReviews = testimonials.length;

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  const starSize = size === 'lg' ? 'w-7 h-7' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`${starSize} ${star <= Math.round(rating) ? 'text-primary' : 'text-white/15'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export default function CoursesPage() {
  useScrollReveal();

  return (
    <div
      style={{ minHeight: '100vh', background: 'transparent', color: '#ffffff', overflowX: 'hidden' }}
    >
      <PageBackdrop src="/assets/images/drum-5.jpg" />
      <Header />

      {/* Grid Overlay */}
      <div className="grid-overlay">
        <div className="grid-inner">
          <div className="grid-line-v" />
          <div className="grid-line-v hidden md:block" />
          <div className="grid-line-v hidden lg:block" />
          <div className="grid-line-v" />
        </div>
      </div>

      {/* Hero with background image */}
      <section className="pt-32 pb-16 border-b border-primary/10 relative overflow-hidden min-h-[420px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src="/assets/images/studio-main.jpg"
            alt="Diggity Glover's studio — desk, monitors and drum kit"
            fill
            className="object-cover object-center"
            priority
          />
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-black/70" />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at top center, rgba(204,0,0,0.18) 0%, rgba(139,0,0,0.10) 40%, transparent 70%)',
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(204,0,0,0.015) 3px, rgba(204,0,0,0.015) 4px)',
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="anim-in-1">
            <span className="section-label block mb-4">// Courses</span>
            <h1
              className="font-display font-extrabold uppercase tracking-tighter text-fg-base mb-4"
              style={{ fontSize: 'clamp(2.5rem, 7vw, 5.5rem)', lineHeight: '0.95' }}
            >
              Level Up Your
              <br />
              <span className="text-gradient-red">Craft.</span>
            </h1>
            <p className="font-body text-muted text-lg max-w-xl leading-relaxed mt-4">
              Industry-focused courses built for metal musicians and producers. Learn the techniques
              behind real records — no fluff, no filler.
            </p>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course, i) =>
              course.available ? (
                /* Available Course Card */
                <div
                  key={course.id}
                  className="reveal group flex flex-col border border-primary/15 bg-bg-card/30 hover:border-primary/40 hover:bg-bg-card/50 transition-all duration-300"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  {/* Card Header */}
                  <div className="p-6 border-b border-primary/10">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <span
                        className={`font-mono text-xs tracking-widest uppercase px-2 py-1 border ${levelColors[course.level] ?? 'text-muted border-border-subtle'}`}
                      >
                        {course.level}
                      </span>
                      <span className="font-mono text-xs text-muted tracking-wider">
                        {course.duration}
                      </span>
                    </div>
                    <h2 className="font-display font-bold text-lg uppercase tracking-tight text-fg-base group-hover:text-primary transition-colors duration-300 leading-tight">
                      {course.title}
                    </h2>
                  </div>

                  {/* Card Body */}
                  <div className="p-6 flex flex-col flex-1 gap-5">
                    <p className="font-body text-muted text-sm leading-relaxed">
                      {course.description}
                    </p>

                    {/* Topics */}
                    <div className="flex flex-wrap gap-2">
                      {course.topics.map((topic) => (
                        <span
                          key={topic}
                          className="font-mono text-xs text-muted/70 border border-border-subtle px-2 py-0.5 tracking-wide"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>

                    {/* Price + CTA */}
                    <div className="mt-auto pt-4 border-t border-primary/10 flex items-center justify-between gap-4">
                      <div>
                        <span className="font-display font-extrabold text-2xl text-green-400">
                          FREE
                        </span>
                      </div>
                      <a
                        href="https://whop.com/dead-quiet-audio-academy/groovecraft/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary px-5 py-2 text-xs whitespace-nowrap"
                      >
                        Enrol Now
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                /* Coming Soon Card */
                <div
                  key={course.id}
                  className="reveal flex flex-col border border-white/5 bg-bg-card/10 opacity-50 relative overflow-hidden"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  {/* Coming Soon Banner */}
                  <div className="absolute top-3 right-3 z-10">
                    <span className="font-mono text-xs tracking-widest uppercase px-3 py-1 border border-white/20 bg-black/60 text-white/50">
                      Coming Soon
                    </span>
                  </div>

                  {/* Card Header */}
                  <div className="p-6 border-b border-white/5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <span className="font-mono text-xs tracking-widest uppercase px-2 py-1 border border-white/10 text-white/30 bg-white/5 blur-sm">
                        {course.level}
                      </span>
                      <span className="font-mono text-xs text-white/20 tracking-wider blur-sm">
                        {course.duration}
                      </span>
                    </div>
                    <h2 className="font-display font-bold text-lg uppercase tracking-tight text-white/30 leading-tight blur-sm">
                      {course.title}
                    </h2>
                  </div>

                  {/* Card Body */}
                  <div className="p-6 flex flex-col flex-1 gap-5">
                    <p className="font-body text-white/20 text-sm leading-relaxed blur-sm">
                      {course.description}
                    </p>

                    {/* Topics */}
                    <div className="flex flex-wrap gap-2">
                      {course.topics.map((topic) => (
                        <span
                          key={topic}
                          className="font-mono text-xs text-white/15 border border-white/10 px-2 py-0.5 tracking-wide blur-sm"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>

                    {/* Price + CTA */}
                    <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between gap-4">
                      <div>
                        <span className="font-display font-extrabold text-2xl text-white/20 blur-sm">
                          {course.price}
                        </span>
                      </div>
                      <span className="font-mono text-xs px-5 py-2 border border-white/10 text-white/25 whitespace-nowrap cursor-not-allowed">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>

          {/* Bottom CTA */}
          <div className="reveal mt-16 border border-primary/20 p-10 text-center relative overflow-hidden">
            {/* Background image */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: "url('/assets/images/diggity-8.jpg')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
            {/* Dark overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'rgba(0,0,0,0.80)' }}
            />
            {/* Glow */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at center, rgba(204,0,0,0.08) 0%, transparent 70%)',
              }}
            />
            <div className="relative z-10">
              <span className="section-label block mb-3">// Custom Training</span>
              <h3 className="font-display font-bold text-2xl uppercase tracking-tight text-fg-base mb-3">
                Need Something Tailored?
              </h3>
              <p className="font-body text-muted text-base max-w-md mx-auto mb-6">
                Looking for 1-on-1 mentoring, a bespoke workshop, or a course bundle? Get in touch
                and let's put something together.
              </p>
              <Link
                href="/contact"
                className="btn-primary px-8 py-3 text-sm inline-flex items-center gap-2"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Ratings & Testimonials ── */}
      <section className="py-20 border-t border-primary/10">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="reveal mb-12 text-center">
            <span className="section-label block mb-4">// Student Reviews</span>
            <h2
              className="font-display font-extrabold uppercase tracking-tighter text-fg-base mb-4"
              style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', lineHeight: '1' }}
            >
              What Students Are <span className="text-gradient-red">Saying.</span>
            </h2>
          </div>

          {/* Overall Rating Block */}
          <div className="reveal mb-14 flex flex-col items-center gap-3">
            <div className="flex items-end gap-4">
              <span className="font-display font-extrabold text-7xl text-fg-base leading-none">
                {overallRating}
              </span>
              <div className="flex flex-col gap-1 pb-2">
                <StarRating rating={overallRating} size="lg" />
                <span className="font-mono text-xs text-muted tracking-widest uppercase">
                  Based on {totalReviews} reviews
                </span>
              </div>
            </div>
            {/* Rating bar breakdown */}
            <div className="mt-4 w-full max-w-xs flex flex-col gap-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = testimonials.filter((t) => t.rating === star).length;
                const pct = Math.round((count / totalReviews) * 100);
                return (
                  <div key={star} className="flex items-center gap-3">
                    <span className="font-mono text-xs text-muted w-3 text-right">{star}</span>
                    <svg
                      className="w-3 h-3 text-primary flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <div className="flex-1 h-1.5 bg-white/5 border border-white/5">
                      <div className="h-full bg-primary/70" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-mono text-xs text-muted w-8">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Testimonial Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                className="reveal flex flex-col gap-4 border border-primary/15 bg-bg-card/30 p-6 hover:border-primary/35 hover:bg-bg-card/50 transition-all duration-300"
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                {/* Stars */}
                <StarRating rating={t.rating} size="sm" />

                {/* Review text */}
                <p className="font-body text-muted text-sm leading-relaxed flex-1">"{t.text}"</p>

                {/* Reviewer info */}
                <div className="pt-4 border-t border-primary/10 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {/* Avatar placeholder */}
                    <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                      <span className="font-display font-bold text-xs text-primary uppercase">
                        {t.name.charAt(0)}
                      </span>
                    </div>
                    <span className="font-display font-bold text-sm text-fg-base uppercase tracking-tight">
                      {t.name}
                    </span>
                  </div>
                  <span className="font-mono text-xs text-muted/60 text-right leading-tight max-w-[120px]">
                    {t.course}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
